import React, { useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, CalendarIcon } from 'lucide-react';
interface AppointmentCalendarProps {
  persona: 'patient' | 'receptionist';
}
const mockAppointments = [{
  id: 1,
  date: '2024-01-15',
  time: '10:00 AM',
  doctor: 'Dr. Sarah Johnson',
  patient: 'John Doe',
  type: 'Consultation',
  room: 'Room 101'
}, {
  id: 2,
  date: '2024-01-15',
  time: '2:00 PM',
  doctor: 'Dr. Michael Chen',
  patient: 'Jane Smith',
  type: 'Follow-up',
  room: 'Room 102'
}, {
  id: 3,
  date: '2024-01-16',
  time: '11:00 AM',
  doctor: 'Dr. Sarah Johnson',
  patient: 'Bob Wilson',
  type: 'Check-up',
  room: 'Room 101'
}];
export function AppointmentCalendar({
  persona
}: AppointmentCalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const months = [];
  const startDate = new Date();
  for (let i = 0; i < 9; i++) {
    const month = new Date(startDate.getFullYear(), startDate.getMonth() + i, 1);
    months.push(month);
  }
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };
  const formatMonthYear = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric'
    });
  };
  const navigateMonth = (direction: 'prev' | 'next') => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() + (direction === 'next' ? 1 : -1));
    setCurrentMonth(newMonth);
  };
  const isToday = (date: Date | null) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };
  const hasAppointment = (date: Date | null) => {
    if (!date) return false;
    const dateStr = date.toISOString().split('T')[0];
    return mockAppointments.some(apt => apt.date === dateStr);
  };
  return <div className="space-y-6" data-id="element-21">
      {/* Calendar Controls */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-22">
        <div className="flex items-center justify-between mb-4" data-id="element-23">
          <button onClick={() => navigateMonth('prev')} className="p-2 hover:bg-gray-100 rounded-lg" data-id="element-24">
            <ChevronLeftIcon className="w-5 h-5" data-id="element-25" />
          </button>
          <h2 className="text-xl font-semibold" data-id="element-26">
            {formatMonthYear(currentMonth)}
          </h2>
          <button onClick={() => navigateMonth('next')} className="p-2 hover:bg-gray-100 rounded-lg" data-id="element-27">
            <ChevronRightIcon className="w-5 h-5" data-id="element-28" />
          </button>
        </div>
        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-2" data-id="element-29">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => <div key={day} className="text-center text-sm font-medium text-gray-600 py-2" data-id="element-30">
              {day}
            </div>)}
          {getDaysInMonth(currentMonth).map((date, index) => <button key={index} onClick={() => date && setSelectedDate(date)} disabled={!date} className={`aspect-square p-2 text-sm rounded-lg transition-colors ${!date ? 'invisible' : isToday(date) ? 'bg-blue-600 text-white font-bold' : hasAppointment(date) ? 'bg-green-100 text-green-800 font-medium hover:bg-green-200' : 'hover:bg-gray-100'}`} data-id="element-31">
              {date?.getDate()}
            </button>)}
        </div>
      </div>
      {/* 9-Month Overview */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-32">
        <h3 className="text-lg font-semibold mb-4 flex items-center space-x-2" data-id="element-33">
          <CalendarIcon className="w-5 h-5 text-blue-600" data-id="element-34" />
          <span data-id="element-35">9-Month Overview</span>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-36">
          {months.map((month, idx) => <button key={idx} onClick={() => setCurrentMonth(month)} className={`p-4 rounded-lg border-2 transition-colors ${month.getMonth() === currentMonth.getMonth() && month.getFullYear() === currentMonth.getFullYear() ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-37">
              <div className="font-semibold text-gray-900" data-id="element-38">
                {formatMonthYear(month)}
              </div>
              <div className="text-sm text-gray-600 mt-1" data-id="element-39">Click to view</div>
            </button>)}
        </div>
      </div>
      {/* Selected Date Appointments */}
      {selectedDate && <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-40">
          <h3 className="text-lg font-semibold mb-4" data-id="element-41">
            Appointments on{' '}
            {selectedDate.toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric'
        })}
          </h3>
          <div className="space-y-3" data-id="element-42">
            {mockAppointments.filter(apt => apt.date === selectedDate.toISOString().split('T')[0]).map(apt => <div key={apt.id} className="p-4 border border-gray-200 rounded-lg" data-id="element-43">
                  <div className="flex items-center justify-between" data-id="element-44">
                    <div data-id="element-45">
                      <div className="font-semibold" data-id="element-46">{apt.time}</div>
                      <div className="text-sm text-gray-600" data-id="element-47">{apt.doctor}</div>
                      {persona === 'receptionist' && <div className="text-sm text-gray-600" data-id="element-48">
                          Patient: {apt.patient}
                        </div>}
                      <div className="text-sm text-gray-600" data-id="element-49">
                        {apt.type} - {apt.room}
                      </div>
                    </div>
                    <div className="flex space-x-2" data-id="element-50">
                      <button className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700" data-id="element-51">
                        Edit
                      </button>
                      <button className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700" data-id="element-52">
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>)}
            {mockAppointments.filter(apt => apt.date === selectedDate.toISOString().split('T')[0]).length === 0 && <p className="text-gray-500 text-center py-4" data-id="element-53">
                No appointments scheduled for this date
              </p>}
          </div>
        </div>}
    </div>;
}